import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Fab,
  Badge,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Button,
  Divider,
  Grow,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddCommentIcon from '@mui/icons-material/AddComment';
import DescriptionIcon from '@mui/icons-material/Description';
import useApi from 'hooks/useApi';
import { ChatThread } from 'types/chat';
import InlineChatWindow from './InlineChatWindow';
import StartChatPanel from './StartChatPanel';

interface FloatingChatWidgetProps {
  notifications: any[];
  userUid?: string;
}

type ViewState = 'list' | 'chat' | 'newChat';

const FloatingChatWidget = ({ notifications, userUid }: FloatingChatWidgetProps) => {
  const { getUserChatThreads, markChatMessagesAsRead, markAllChatsAsRead, transactionOrder, showToast } = useApi();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ViewState>('list');

  // Thread list state
  const [threads, setThreads] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  // Active chat state
  const [activeThread, setActiveThread] = useState<ChatThread | null>(null);
  const [activeTransactionKey, setActiveTransactionKey] = useState<string | undefined>();

  // New chat state — Step 0 is transaction search, then StartChatPanel handles the rest
  const [selectedTxn, setSelectedTxn] = useState<any>(null);
  const [txnSearch, setTxnSearch] = useState('');
  const [txnResults, setTxnResults] = useState<any[]>([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnPage, setTxnPage] = useState(1);
  const [txnHasMore, setTxnHasMore] = useState(false);
  const [txnLoadingMore, setTxnLoadingMore] = useState(false);
  const [txnTotal, setTxnTotal] = useState(0);

  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const txnSearchTimer = useRef<NodeJS.Timeout | null>(null);

  // ==================== Draggable FAB ====================
  const STORAGE_KEY = 'chat-fab-pos';
  const getInitialPos = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return { right: 24, bottom: 24 };
  };
  const [fabPos, setFabPos] = useState<{ right: number; bottom: number }>(getInitialPos);
  const dragRef = useRef<{ startX: number; startY: number; startRight: number; startBottom: number } | null>(null);
  const isDragging = useRef(false);

  const onFabMouseDown = (e: React.MouseEvent) => {
    if (open) return;
    e.preventDefault();
    isDragging.current = false;
    dragRef.current = { startX: e.clientX, startY: e.clientY, startRight: fabPos.right, startBottom: fabPos.bottom };

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) isDragging.current = true;
      const newRight = Math.max(8, Math.min(window.innerWidth - 160, dragRef.current.startRight - dx));
      const newBottom = Math.max(8, Math.min(window.innerHeight - 56, dragRef.current.startBottom - dy));
      setFabPos({ right: newRight, bottom: newBottom });
    };

    const onMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (dragRef.current) {
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        const finalPos = {
          right: Math.max(8, Math.min(window.innerWidth - 160, dragRef.current.startRight - dx)),
          bottom: Math.max(8, Math.min(window.innerHeight - 56, dragRef.current.startBottom - dy)),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(finalPos));
      }
      dragRef.current = null;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // ==================== Thread List Logic ====================

  const loadThreads = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      const response = await getUserChatThreads(pageNum, 15);
      if (response.success) {
        const { threads: t, hasMore: m, totalUnread: u } = response.data;
        if (append) setThreads(prev => [...prev, ...(t || [])]);
        else setThreads(t || []);
        setHasMore(m);
        setTotalUnread(u || 0);
      }
    } catch (e) { console.error('Error loading threads:', e); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [getUserChatThreads]);

  const silentRefresh = useCallback(async () => {
    try {
      const response = await getUserChatThreads(1, Math.max(15, threads.length));
      if (response.success) {
        setThreads(response.data.threads || []);
        setTotalUnread(response.data.totalUnread || 0);
      }
    } catch { /* silent */ }
  }, [getUserChatThreads, threads.length]);

  // Load all threads (used when Unread Only filter is activated)
  const loadAllThreads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getUserChatThreads(1, 500);
      if (response.success) {
        setThreads(response.data.threads || []);
        setHasMore(false);
        setTotalUnread(response.data.totalUnread || 0);
      }
    } catch (e) { console.error('Error loading all threads:', e); }
    finally { setLoading(false); }
  }, [getUserChatThreads]);

  useEffect(() => { loadThreads(1); }, [loadThreads]);

  useEffect(() => {
    if (open && view === 'list') {
      pollRef.current = setInterval(silentRefresh, 8000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, view, silentRefresh]);

  useEffect(() => {
    if (notifications.length > 0) silentRefresh();
  }, [notifications.length, silentRefresh]);

  // ==================== Navigation ====================

  const handleToggle = () => {
    if (!open) { loadThreads(1); setView('list'); setSearchQuery(''); }
    setOpen(prev => !prev);
  };

  const handleClose = () => { setOpen(false); setView('list'); };

  const handleOpenThread = (thread: any) => {
    setActiveThread(thread);
    setActiveTransactionKey(thread.authkey?.key);
    setView('chat');
    if (thread.uid) markChatMessagesAsRead(thread.uid, { markAll: true }).catch(() => {});
  };

  const handleBackToList = () => { setView('list'); setActiveThread(null); silentRefresh(); };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      const next = page + 1; setPage(next); loadThreads(next, true);
    }
  };

  // ==================== New Chat Logic ====================

  const handleStartNewChat = () => {
    setView('newChat');
    setSelectedTxn(null);
    setTxnSearch('');
    searchTransactions('');
  };

  const searchTransactions = useCallback(async (query: string, pageNum: number = 1, append: boolean = false) => {
    if (pageNum === 1) setTxnLoading(true);
    else setTxnLoadingMore(true);
    try {
      const pageSize = 30;
      const response = await transactionOrder({
        page: pageNum, size: pageSize,
        ...(query.trim() ? { search: query.trim() } : {}),
        teams: ['all'],
        from: '01/01/2020',
        to: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      });
      if (response.success) {
        const rows = response.data?.rows || [];
        const total = response.data?.filters?.count || 0;
        setTxnTotal(total);
        if (append) {
          setTxnResults(prev => [...prev, ...rows]);
        } else {
          setTxnResults(rows);
        }
        setTxnPage(pageNum);
        setTxnHasMore(pageNum * pageSize < total);
      }
    } catch (e) { console.error('Error searching transactions:', e); }
    finally { setTxnLoading(false); setTxnLoadingMore(false); }
  }, [transactionOrder]);

  const handleLoadMoreTransactions = () => {
    if (txnHasMore && !txnLoadingMore) {
      searchTransactions(txnSearch, txnPage + 1, true);
    }
  };

  useEffect(() => {
    if (txnSearchTimer.current) clearTimeout(txnSearchTimer.current);
    if (view === 'newChat' && !selectedTxn) {
      txnSearchTimer.current = setTimeout(() => searchTransactions(txnSearch), 400);
    }
    return () => { if (txnSearchTimer.current) clearTimeout(txnSearchTimer.current); };
  }, [txnSearch, view, selectedTxn, searchTransactions]);

  const handleSelectTransaction = (txn: any) => { setSelectedTxn(txn); };

  const handleNewChatCreated = (thread: ChatThread) => {
    const txnKey = selectedTxn?.key;
    const txnUid = selectedTxn?.uid;
    const enrichedThread = {
      ...thread,
      authkey: { key: txnKey, uid: txnUid, teamUid: selectedTxn?.team?.uid || '' },
    };
    setActiveThread(enrichedThread as any);
    setActiveTransactionKey(txnKey);
    setView('chat');
    silentRefresh();
  };

  // ==================== Helpers ====================

  const navigateToTransaction = (thread: any) => {
    const teamUid = thread.authkey?.teamUid || thread.teamUid;
    const txnUid = thread.authkey?.uid || thread.transactionId;
    const txnKey = thread.authkey?.key || thread.transactionKey;
    if (teamUid) {
      handleClose();
      navigate(`/transactions/details/${teamUid}/${txnUid || ''}/${txnKey || ''}`);
    } else if (txnUid) {
      // Fallback: navigate without teamUid using transactionId in URL
      handleClose();
      navigate(`/transactions/details/_/${txnUid}/${txnKey || ''}`);
    } else {
      showToast('Transaction details unavailable', 'warning');
    }
  };

  const formatRelativeTime = (dateString: string) => {
    if (!dateString) return '';
    const diff = Date.now() - new Date(dateString).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'now';
    if (min < 60) return `${min}m`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getInitials = (name: string) => {
    if (!name) return 'U';
    const w = name.split(' ');
    return w.length >= 2 ? `${w[0][0]}${w[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  const getLastMessagePreview = (thread: any) => {
    const m = thread.lastMessage;
    if (!m) return 'No messages yet';
    const sender = m.senderName ? `${m.senderName.split(' ')[0]}: ` : '';
    const combined = `${sender}${m.message || ''}`;
    return combined.length > 80 ? combined.substring(0, 80) + '...' : combined;
  };

  const filteredThreads = threads.filter(t => {
    // Unread filter
    if (unreadOnly && (!t.unreadCount || t.unreadCount === 0)) return false;
    // Search filter
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (t.subject || '').toLowerCase().includes(q) ||
      (t.authkey?.key || '').toLowerCase().includes(q) ||
      (t.transactionId || '').toLowerCase().includes(q) ||
      (t.lastMessage?.message || '').toLowerCase().includes(q);
  });


  const handleMarkAllRead = async () => {
  if (markingAllRead || totalUnread === 0) return;

  const previousThreads = threads;
  const previousTotalUnread = totalUnread;

  setMarkingAllRead(true);

  // optimistic update for visible page
  setThreads((prev) => prev.map((thread) => ({ ...thread, unreadCount: 0 })));
  setTotalUnread(0);

  try {
    const response = await markAllChatsAsRead();
    const messagesMarked = response?.data?.messagesMarkedAsRead ?? 0;

    showToast(
      messagesMarked > 0
        ? `Marked ${messagesMarked} messages as read.`
        : 'All accessible messages marked as read.',
      'success'
    );

    // reload from source of truth
    setPage(1);
    if (unreadOnly) {
      await loadAllThreads();
    } else {
      await loadThreads(1, false);
    }
  } catch {
    setThreads(previousThreads);
    setTotalUnread(previousTotalUnread);
    showToast('Could not mark messages as read.', 'error');
  } finally {
    setMarkingAllRead(false);
  }
};

  // ==================== New Chat View ====================

  const renderNewChatView = () => {
    // Once a transaction is selected, delegate to StartChatPanel (same UI as CommunicationSidebar)
    if (selectedTxn) {
      const txnUid = selectedTxn.uid || selectedTxn.transactionId;
      const txnKey = selectedTxn.key || selectedTxn.transactionKey;
      return (
        <StartChatPanel
          onBack={() => setSelectedTxn(null)}
          transactionId={txnUid}
          transactionKey={txnKey}
          onChatCreated={handleNewChatCreated}
          embedded={false}
        />
      );
    }

    // Step 0: Select a transaction
    return (
    <>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={handleBackToList} sx={{ color: 'white' }}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>New Chat</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <>
            <Box sx={{ p: 2 }}>
              <TextField fullWidth size="small" placeholder="Search transaction by name or key..."
                value={txnSearch} onChange={(e) => setTxnSearch(e.target.value)} autoFocus
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'grey.50' } }}
              />
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {txnLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
              ) : txnResults.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No transactions found</Typography>
                </Box>
              ) : (
                <>
                {!txnSearch.trim() && (
                  <Typography variant="caption" sx={{ px: 2, py: 0.75, display: 'block', bgcolor: 'grey.50', color: 'text.secondary', fontWeight: 600, fontSize: 11 }}>
                    Recent Transactions
                  </Typography>
                )}
                <List disablePadding>
                  {txnResults.map((txn: any, idx: number) => (
                    <Box key={txn.uid || txn._id || idx}>
                      <ListItemButton onClick={() => handleSelectTransaction(txn)} sx={{ px: 2, py: 1.25 }}>
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', color: 'primary.main', fontSize: 13 }}>
                            <DescriptionIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ fontSize: 14 }}>
                              {txn.key || 'Transaction'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {txn.team?.name || ''}{txn.displayStatus ? ` - ${txn.displayStatus}` : ''}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                      {idx < txnResults.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
                {/* Show More button for pagination */}
                {txnHasMore && (
                  <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Button size="small" variant="outlined" onClick={handleLoadMoreTransactions} disabled={txnLoadingMore}
                      sx={{ textTransform: 'none', borderRadius: 2 }}>
                      {txnLoadingMore ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                      {txnLoadingMore ? 'Loading...' : `Show More (${txnTotal - txnResults.length} more)`}
                    </Button>
                  </Box>
                )}
                {!txnHasMore && txnResults.length > 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1.5, fontSize: 10 }}>
                    All transactions loaded ({txnResults.length})
                  </Typography>
                )}
                </>
              )}
            </Box>
          </>
      </Box>
    </>
    );
  };

  // ==================== Thread List View ====================

  const renderListView = () => (
    <>
      {/* Header */}
      <Box sx={{
        p: 2, bgcolor: 'primary.main', color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>Chats</Typography>
          {totalUnread > 0 && (
            <Chip label={totalUnread} size="small" sx={{ bgcolor: 'error.main', color: 'white', height: 24, fontSize: 12, fontWeight: 700 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button onClick={handleStartNewChat} variant="text" size="small"
            startIcon={<AddCommentIcon sx={{ fontSize: 18 }} />}
            sx={{ color: 'white', textTransform: 'none', fontWeight: 600, fontSize: 13,
              '& .MuiButton-startIcon': { color: 'white' },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' } }}>
            Start New
          </Button>
          <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Search + Filter */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField fullWidth size="small" placeholder="Search chats..."
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'grey.50' } }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              label={unreadOnly ? `Unread Only (${threads.filter(t => t.unreadCount > 0).length})` : 'Unread Only'}
              size="small"
              color={unreadOnly ? 'primary' : 'default'}
              variant={unreadOnly ? 'filled' : 'outlined'}
              clickable
              onClick={() => {
                const newValue = !unreadOnly;
                setUnreadOnly(newValue);
                if (newValue) {
                  loadAllThreads();
                } else {
                  loadThreads(1);
                  setPage(1);
                }
              }}
              sx={{ height: 24, fontSize: 11, fontWeight: 600 }}
            />
            {unreadOnly && (
              <Chip label="Clear" size="small" variant="outlined" clickable
                onClick={() => { setUnreadOnly(false); loadThreads(1); setPage(1); }}
                sx={{ height: 24, fontSize: 11 }}
              />
            )}
          </Box>

          <Button
            size="small"
            variant="text"
            onClick={handleMarkAllRead}
            disabled={totalUnread === 0 || markingAllRead}
            sx={{ textTransform: 'none', minWidth: 110 }}
          >
            {markingAllRead ? 'Marking...' : 'Mark all read'}
          </Button>
        </Box>
      </Box>

      {/* Thread List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress size={32} />
          </Box>
        ) : filteredThreads.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', flex: 1, p: 3, textAlign: 'center', minHeight: 250,
          }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
            <Typography color="text.secondary">
              {unreadOnly ? 'No unread chats' : (searchQuery ? 'No chats found' : 'No conversations yet')}
            </Typography>
            <Button size="small" startIcon={<AddCommentIcon />} onClick={handleStartNewChat}
              sx={{ mt: 2, textTransform: 'none' }}>
              Start a new chat
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {filteredThreads.map((thread, index) => {
              const hasMention = thread.lastMessageHasMention === true;
              const unread = thread.unreadCount || 0;
              return (
                <Box key={thread.uid}>
                  <ListItemButton onClick={(e) => {
                    if ((e.target as HTMLElement).closest('[data-txn-link]')) { navigateToTransaction(thread); return; }
                    handleOpenThread(thread);
                  }} sx={{
                    py: 1.5, px: 2,
                    bgcolor: hasMention ? 'rgba(211, 47, 47, 0.04)' : 'transparent',
                    borderLeft: hasMention ? '4px solid' : '4px solid transparent',
                    borderLeftColor: hasMention ? 'error.main' : 'transparent',
                    '&:hover': { bgcolor: hasMention ? 'rgba(211, 47, 47, 0.08)' : 'action.hover' },
                  }}>
                    <ListItemAvatar sx={{ minWidth: 52 }}>
                      <Badge badgeContent={unread} color="error" overlap="circular" invisible={unread === 0}
                        sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 18, height: 18 } }}>
                        <Avatar sx={{ width: 42, height: 42, fontSize: 14, bgcolor: 'primary.light', color: 'primary.main' }}>
                          {(thread.participants?.length || 0) > 2
                            ? <GroupIcon sx={{ fontSize: 20 }} />
                            : getInitials(thread.subject || 'CH')}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={unread > 0 ? 700 : 500} noWrap sx={{ maxWidth: 200 }}>
                              {thread.subject || 'Chat Thread'}
                            </Typography>
                            {hasMention && (
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: 'error.main', color: 'white', borderRadius: '4px', px: 0.5, py: 0.15, flexShrink: 0,
                              }}>
                                <AlternateEmailIcon sx={{ fontSize: 11 }} />
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                            {thread.authkey?.key && (
                              <Box component="span" data-txn-link="true"
                                sx={{
                                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  color: 'white', bgcolor: 'primary.main',
                                  px: 0.75, py: 0.25, borderRadius: 0.5,
                                  '&:hover': { bgcolor: 'primary.dark' },
                                }}>
                                {thread.authkey.key}
                              </Box>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                              {formatRelativeTime(thread.lastMessageAt)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.25 }}>
                          <Typography variant="caption" noWrap
                            color={unread > 0 ? 'text.primary' : 'text.secondary'}
                            sx={{ display: 'block', fontWeight: unread > 0 ? 600 : 400 }}>
                            {getLastMessagePreview(thread)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  {index < filteredThreads.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        )}

        {hasMore && !loading && !unreadOnly && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button size="small" variant="outlined" onClick={handleLoadMore} disabled={loadingMore}
              sx={{ textTransform: 'none', borderRadius: 2 }}>
              {loadingMore ? 'Loading...' : 'Show More'}
            </Button>
          </Box>
        )}
        {!hasMore && threads.length > 0 && !loading && !unreadOnly && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
            No more conversations
          </Typography>
        )}
      </Box>
    </>
  );

  // ==================== Render ====================

  return (
    <ClickAwayListener onClickAway={() => { if (open) handleClose(); }}>
      <Box sx={{ position: 'fixed', bottom: fabPos.bottom, right: fabPos.right, zIndex: 1300 }}>
        <Grow in={open} style={{ transformOrigin: 'bottom right' }} timeout={250}>
          <Paper elevation={20} sx={{
            position: 'absolute', bottom: 0, right: 0,
            width: { xs: 'calc(100vw - 32px)', sm: 420 },
            height: { xs: 'calc(100vh - 100px)', sm: 'min(85vh, 780px)' },
            borderRadius: 3, display: 'flex', flexDirection: 'column',
            overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
          }}>
            {view === 'chat' && activeThread ? (
              <InlineChatWindow open={true} onClose={handleBackToList} onClosePanel={handleClose}
                thread={activeThread} transactionKey={activeTransactionKey} embedded={true} />
            ) : view === 'newChat' ? (
              renderNewChatView()
            ) : (
              renderListView()
            )}
          </Paper>
        </Grow>

        <Tooltip title="Start Chat" placement="left">
          <Fab
            color="primary"
            onMouseDown={onFabMouseDown}
            onClick={() => { if (!isDragging.current) handleToggle(); }}
            sx={{
              position: 'relative',
              cursor: 'grab',
              boxShadow: open ? 'none' : '0 4px 20px rgba(0,0,0,0.25)',
              opacity: open ? 0 : 1, transform: open ? 'scale(0)' : 'scale(1)',
              transition: 'opacity 0.25s ease, transform 0.25s ease',
              pointerEvents: open ? 'none' : 'auto',
              userSelect: 'none',
            }}
          >
            <Badge badgeContent={totalUnread} color="error" max={99} invisible={totalUnread === 0}
              sx={{ '& .MuiBadge-badge': { fontSize: 11, minWidth: 20, height: 20, fontWeight: 700 } }}>
              <ChatIcon />
            </Badge>
          </Fab>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

export default FloatingChatWidget;
